import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Chip,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Replay as ReplayIcon,
  AssignmentOutlined as CreateIcon,
  NotificationsActiveOutlined as NotifyIcon,
  PhotoCameraOutlined as CaptureIcon,
  PictureAsPdfOutlined as PdfIcon,
  FactCheckOutlined as ApproveIcon,
  ReceiptLongOutlined as DeliverIcon,
  CheckCircle as CheckIcon,
  PlaceOutlined as GpsIcon,
} from '@mui/icons-material';
import Seo from '../components/Seo';

const steps = [
  { label: 'Create', role: 'Office Staff · Web', icon: <CreateIcon /> },
  { label: 'Assign', role: 'System · Push', icon: <NotifyIcon /> },
  { label: 'Capture', role: 'Inspector · Mobile', icon: <CaptureIcon /> },
  { label: 'Generate', role: 'System · Cloud', icon: <PdfIcon /> },
  { label: 'Approve', role: 'Manager · Web', icon: <ApproveIcon /> },
  { label: 'Deliver', role: 'Client · Portal', icon: <DeliverIcon /> },
];

// Mock screen for each step
const MockScreen = ({ step, navigate }) => {
  const cardSx = { bgcolor: 'white', borderRadius: 3, p: { xs: 3, md: 4 }, boxShadow: '0 12px 40px rgba(15,39,51,0.12)' };

  switch (step) {
    case 0:
      return (
        <Box sx={cardSx}>
          <Chip label="New Inspection" color="primary" size="small" sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>Create & assign in seconds</Typography>
          <Stack spacing={1.5}>
            {[
              ['Client', 'Meridian Commodities Pvt. Ltd.'],
              ['Service', 'Pre-Dispatch Loading Inspection'],
              ['Location', 'Mundra Port, Gujarat'],
              ['Assign to', 'A. Sharma (Cargo Surveyor)'],
            ].map(([k, v]) => (
              <Box key={k} sx={{ p: 1.5, bgcolor: '#f5f8fa', borderRadius: 1.5 }}>
                <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7a8c95', fontWeight: 700 }}>{k}</Typography>
                <Typography sx={{ fontSize: '0.95rem', color: '#0f2733', fontWeight: 500 }}>{v}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      );
    case 1:
      return (
        <Box sx={cardSx}>
          <Typography variant="h6" gutterBottom>Inspector notified instantly</Typography>
          <Box sx={{ p: 2, bgcolor: '#013e5c', color: 'white', borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <NotifyIcon sx={{ color: 'secondary.light' }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>New inspection assigned</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)' }}>
                Pre-Dispatch Loading · Mundra Port · Tap to open
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            A real-time push notification reaches the inspector's phone. Tapping it opens the
            inspection directly in the mobile app.
          </Typography>
        </Box>
      );
    case 2:
      return (
        <Box sx={cardSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Field capture</Typography>
            <Chip icon={<GpsIcon />} label="Offline-ready" size="small" sx={{ bgcolor: 'rgba(1,86,122,0.08)', color: 'primary.main', fontWeight: 700, '& .MuiChip-icon': { color: 'primary.main' } }} />
          </Stack>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {['Hold condition', 'Bag condition', 'Loading', 'Seal'].map((slot, i) => (
              <Grid item xs={6} key={slot}>
                <Box sx={{ aspectRatio: '4/3', bgcolor: i < 3 ? 'rgba(1,86,122,0.08)' : '#f0f3f5', borderRadius: 1.5, border: '1px dashed rgba(1,86,122,0.3)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
                  {i < 3 ? <CheckIcon /> : <CaptureIcon />}
                  <Typography sx={{ fontSize: '0.7rem', mt: 0.5, color: '#52707d' }}>{slot}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="text.secondary">
            Inspectors capture photos into designated slots, complete checklists and tally cargo —
            with a geotagged selfie verifying on-site presence. All works offline and syncs later.
          </Typography>
        </Box>
      );
    case 3:
      return (
        <Box sx={cardSx}>
          <Typography variant="h6" gutterBottom>Report generated automatically</Typography>
          <Box sx={{ p: 3, bgcolor: '#f5f8fa', borderRadius: 2, textAlign: 'center' }}>
            <PdfIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 700, mt: 1 }}>IQ-2026-PDL-0481.pdf</Typography>
            <Typography variant="body2" color="text.secondary">Branded · Photos · Tally · Checklist</Typography>
            <Chip label="Generated in < 3 seconds" color="secondary" size="small" sx={{ mt: 1.5, fontWeight: 700 }} />
          </Box>
          <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/platform/demo-report')}>
            View the sample report
          </Button>
        </Box>
      );
    case 4:
      return (
        <Box sx={cardSx}>
          <Typography variant="h6" gutterBottom>Manager approval</Typography>
          <Box sx={{ p: 2, border: '1px solid #e3e9ec', borderRadius: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>IQ-2026-PDL-0481</Typography>
                <Typography variant="caption" color="text.secondary">Meridian Commodities · A. Sharma</Typography>
              </Box>
              <Chip label="Pending" size="small" sx={{ bgcolor: 'rgba(201,162,75,0.15)', color: '#a07f2f', fontWeight: 700 }} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="contained" sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>Approve</Button>
            <Button fullWidth variant="outlined" color="error">Reject</Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Managers preview the PDF and approve, or reject with feedback that notifies the inspector
            to correct and resubmit.
          </Typography>
        </Box>
      );
    case 5:
      return (
        <Box sx={cardSx}>
          <Typography variant="h6" gutterBottom>Delivered & invoiced</Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, bgcolor: '#f5f8fa', borderRadius: 1.5 }}>
              <CheckIcon sx={{ color: '#2e7d32' }} />
              <Typography sx={{ fontSize: '0.9rem' }}>Report delivered to client portal & email</Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, bgcolor: '#f5f8fa', borderRadius: 1.5 }}>
              <DeliverIcon sx={{ color: 'primary.main' }} />
              <Typography sx={{ fontSize: '0.9rem' }}>Invoice auto-generated · client-specific pricing</Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The client views the report and invoice in their self-service portal. The full cycle is
            logged in an immutable audit trail.
          </Typography>
        </Box>
      );
    default:
      return null;
  }
};

const DemoSimulation = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const isLast = active === steps.length - 1;

  return (
    <Box sx={{ bgcolor: '#eef3f5', minHeight: '70vh', py: { xs: 4, md: 6 } }}>
      <Seo
        title="Interactive Platform Demo"
        description="Step through a full inspection workflow on the InspectIQ platform — from client request and field capture to automated report, approval, delivery and invoicing."
        path="/platform/demo"
      />
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip label="Interactive Demo" color="secondary" size="small" sx={{ fontWeight: 700, mb: 1.5 }} />
          <Typography variant="h3" gutterBottom>See the workflow in action</Typography>
          <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto' }}>
            Step through a single inspection as it moves from request to delivery across all four roles.
          </Typography>
        </Box>

        {/* Progress */}
        <LinearProgress
          variant="determinate"
          value={((active + 1) / steps.length) * 100}
          sx={{ height: 6, borderRadius: 3, mb: 3, bgcolor: 'rgba(1,86,122,0.12)', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }}
        />

        {/* Step dots */}
        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mb: 4 }}>
          {steps.map((s, i) => (
            <Box
              key={s.label}
              onClick={() => setActive(i)}
              sx={{
                flex: 1,
                cursor: 'pointer',
                textAlign: 'center',
                opacity: i === active ? 1 : 0.5,
                transition: 'opacity 0.2s',
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  mx: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: i <= active ? 'primary.main' : 'white',
                  color: i <= active ? 'white' : 'primary.main',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  mb: 0.5,
                }}
              >
                {s.icon}
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.primary', display: { xs: 'none', sm: 'block' } }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Active step label */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="overline" sx={{ color: 'secondary.dark' }}>
            Step {active + 1} of {steps.length} · {steps[active].role}
          </Typography>
        </Box>

        {/* Mock screen */}
        <MockScreen step={active} navigate={navigate} />

        {/* Controls */}
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            disabled={active === 0}
            onClick={() => setActive((a) => Math.max(0, a - 1))}
          >
            Back
          </Button>
          {isLast ? (
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" startIcon={<ReplayIcon />} onClick={() => setActive(0)}>
                Replay
              </Button>
              <Button variant="contained" onClick={() => navigate('/contact')}>
                Request a Demo
              </Button>
            </Stack>
          ) : (
            <Button variant="contained" endIcon={<NextIcon />} onClick={() => setActive((a) => Math.min(steps.length - 1, a + 1))}>
              Next
            </Button>
          )}
        </Stack>

        {/* Back to platform */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button variant="text" startIcon={<BackIcon />} onClick={() => navigate('/platform')}>
            Back to Platform overview
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DemoSimulation;
